import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { FiHome } from 'react-icons/fi';
import ChartFrame, { CHART_PALETTE, TOOLTIP_STYLE } from './ChartFrame';
import { PMS_LABELS, fmtNumber } from '../derive';

// Properties-by-PMS bar chart. When backend §B is not live yet
// (`propertiesAvailable === false`) we render a clear, on-brand notice
// instead of a fake chart.
export default function CanvasProperties({ properties = [], propertiesAvailable, onSelectPms }) {
  const data = useMemo(() => {
    const m = new Map();
    properties.forEach((p) => {
      const k = p?.propertySource || 'unknown';
      m.set(k, (m.get(k) || 0) + 1);
    });
    return Array.from(m.entries())
      .map(([k, count]) => ({ id: k, label: PMS_LABELS[k] || k, count }))
      .sort((a, b) => b.count - a.count);
  }, [properties]);

  const total = data.reduce((s, d) => s + d.count, 0);

  if (!propertiesAvailable) {
    return (
      <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-amber-300 bg-amber-50/40 p-8 text-center">
        <div>
          <FiHome className="mx-auto h-6 w-6 text-amber-500" />
          <p className="mt-2 text-sm font-semibold text-amber-900">Property timestamps unavailable</p>
          <p className="mt-1 text-xs text-amber-700">
            Backend extension required (extending property summary with <code>propertySource</code> + <code>createdAt</code>) to power this view.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ChartFrame
      title="Properties by PMS"
      subtitle={`${fmtNumber(total)} properties · click a bar for details`}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 10, right: 8, bottom: 0, left: -16 }}>
          <CartesianGrid stroke="#f4f4f5" vertical={false} />
          <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#71717a' }} />
          <YAxis tick={{ fontSize: 10, fill: '#71717a' }} allowDecimals={false} />
          <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(99,102,241,0.06)' }} />
          <Bar dataKey="count" radius={[4, 4, 0, 0]} isAnimationActive={false}>
            {data.map((d, i) => (
              <Cell
                key={d.id}
                fill={CHART_PALETTE[i % CHART_PALETTE.length]}
                cursor="pointer"
                onClick={() => onSelectPms?.(d.id)}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartFrame>
  );
}
